import { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { compact, union, uniq } from "lodash";
import { setSelectionColor, identifierToCoords, coordsToIdentifier } from "@utils";
import { useXlsImporterSettings } from "@tools";
import { modulesLegend } from "./modulesLegend";
import { ButtonSwitch } from "@components";
import { Grid, useTheme } from "@material-ui/core";
import { DescriptionColumnSelector, TableDataSelector, WorkTypeColumnSelector } from "./options";
import { FastModeConfigurations } from "./components";
import { Spreadsheet } from "../../../index";
import useStyle from "../Steps.style";
import classnames from "classnames";

export const Breakdown = observer(() => {
  
  const { t } = useTranslation();
  const settings = useXlsImporterSettings();
  const classes = useStyle();
  const theme = useTheme();
  
  const [ columnMode, setColumnMode ] = useState(null);
  const [ moduleMode, setModuleMode ] = useState(null);
  
  const spreadsheet = useRef(null);

  const {
    currentBreakdownData,
    importModules,
    descColumn,
    importWorkTypes,
    titleColumnsLength,
    useFastMode,
    fastModeMapping,
    usedConfiguration,
  } = settings;
  
  const { sheet, selection } = currentBreakdownData;
  const additionalTextColumns = useMemo(
    () => useFastMode ? settings.additionalTextColumns : [],
    [useFastMode, settings.additionalTextColumns]
  );

  useEffect(() => {
    if(spreadsheet.current) {
      clearInitialCellStyles(true);
      getInitialCellStyles(true);
    }
  }, [useFastMode, usedConfiguration]);

  const mergeCells = Object.keys(sheet.mergeCells).map((x) => [
    ...identifierToCoords(x),
    ...sheet.mergeCells[x],
  ]);
  
  const setSpreadsheet = (s) => {
    spreadsheet.current = s;
    getInitialCellStyles(true);
  }

  const handleModuleSelection = (instance, x1, y1, x2, y2) => {
    //x -> column, y -> row

    const type = modulesLegend[moduleMode];
    const mergedCell = sheet.mergeCells[coordsToIdentifier(x1, y1)];
    const singleModule =
      (y2 - y1 === 0 && x2 - x1 === 0) ||
      (mergedCell &&
        x2 - x1 + 1 === mergedCell[0] &&
        y2 - y1 + 1 === mergedCell[1])
        ? importModules.find((s) => s.x === x1 && s.y === y1)
        : null; // moduleMode === -1;

    const clear = Boolean(
      singleModule && (moduleMode === null || singleModule.type === type)
    );

    if (clear || moduleMode !== null)
      [...Array(y2 - y1 + 1).keys()].forEach((y) => {
        const row = y + y1;
        if (isInWorkTypeColl(x1)) return;

        settings.changeImportModules(row, x1, type, clear);

        setSelectionColor(
          [row],
          [x1],
          clear ? null : theme.props.selections[`level${moduleMode}`],
          spreadsheet.current
        );
      });
  };

  const handleColumnSelectionClick = (instance, x1) => handleColumnSelection(x1);
  
  const handleColumnSelection = (col, colIndex = null, isMax = false) => {
    const taken = importWorkTypes.find(
      (s) => s.column === col || s.columnMax === col
    );
    if (taken || (col < titleColumnsLength && col >= 0)) return;

    const t = [...importWorkTypes];
    const i = colIndex !== null ? colIndex : columnMode;
    if (isMax && typeof t[i].columnMax === "number" && t[i].columnMax !== -1)
      colorColumn([t[i].columnMax], true, true);
    if (!isMax && typeof t[i].column === "number")
      colorColumn([t[i].column], true, true);
    
    if (isMax) t[i].columnMax = col;
    else t[i].column = col;
    settings.setImportWorkTypes(t);

    colorColumn([col]);
  };
  
  const clearInitialCellStyles = (apply=false) => {
    // return colorColumn(
    //   [...Array(Math.max(selection[2] - selection[0] + 1, 0)).keys()],
    //   apply,
    //   true,
    //   "initial"
    // );
    return setSelectionColor(
      [...Array(selection[3] - selection[1] + 1).keys()],
      [...Array(Math.max(selection[2] - selection[0] + 1, 0)).keys()],
      "initial",
      apply ? spreadsheet.current : null
    );
  }

  const getInitialCellStyles = (apply=false) => {
    let updates = {};

    if(useFastMode)
      updates = {
        ...updates,
        ...fastModeMapping
          .reduce((a, column, i) => (
            column === null
              ? a
              : { ...a, ...colorColumn([column], apply, false, theme.props.selections[`level${i}`]) }
          ), {})
      };
    else if (importModules.length)
      updates = {
        ...updates,
        ...importModules.reduce((u, m) => {
          const n = setSelectionColor(
            [m.y],
            [m.x],
            theme.props.selections[`level${modulesLegend.indexOf(m.type)}`],
            apply ? spreadsheet.current : null
          );
          return { ...u, ...n };
        }, {}),
      };

    if (importWorkTypes.length)
      updates = {
        ...updates,
        ...colorColumn(
          importWorkTypes.reduce((a, { column, columnMax }) => [
            ...a,
            column,
            ...(columnMax >= 0 ? [columnMax] : []),
          ], []).filter(c => c !== null),
          apply
        ),
      };
    
    if(typeof descColumn === "number") {
      colorColumn([descColumn], true, false, theme.props.selections.alt);
    }

    if(additionalTextColumns.length)
      additionalTextColumns.forEach(({ column }) => {
        if(typeof column === "number")
          colorColumn([column], true, false, theme.props.selections.alt);
      });

    return updates;
  };

  const colorColumn = (cols, apply = true, removeColor = false, moduleColor) => {
    let rows = [...Array(selection[3] - selection[1] + 1).keys()];
    rows = rows.filter((x) => !isInMergeCells(x, cols, !moduleColor && !removeColor));

    return setSelectionColor(
      rows,
      cols,
      removeColor
        ? null
        : (moduleColor || theme.props.selections.alt),
      apply ? spreadsheet.current : null
    );
  };

  const isInMergeCells = (row, cols, checkWholeSpectrum=false) => {
    return !!mergeCells.find((c) => (
      c[2] > 1 &&
      row >= c[1] &&
      row < c[1] + c[3] &&
      cols.reduce((p, x1) => (
        p || (
          (checkWholeSpectrum ? x1 >= c[0] : x1 > c[0]) && x1 < c[0] + c[2]
        )
      ), false)
    ));
  };

  const isInWorkTypeColl = (col) =>
    importWorkTypes.reduce((p, c) => p || c.column === col, false);
  
  const takenColumns = useMemo(() => union(
    compact(importWorkTypes.reduce((l, s) => [
      ...l,
      ...(typeof s.column === "number" ? [s.column] : []),
      ...(typeof s.columnMax === "number" && s.columnMax !== -1 ? [s.columnMax] : []),
    ], [])),
    typeof descColumn === "number" ? [descColumn] : [],
    additionalTextColumns
      .map(({ column }) => column)
      .filter(column => typeof column === "number"),
    useFastMode
      ? fastModeMapping.filter(m => m !== null)
      : uniq(importModules.map((m) => (m.x)))
  ), [ importWorkTypes, descColumn, additionalTextColumns, importModules, useFastMode, fastModeMapping ]);
  
  const availableColumns = useMemo(() => [
    ...Array(Math.max(selection[2] - selection[0] + 1, 0)).keys()
  ], [selection]);
  
  const modesProps = {
    columnMode,
    moduleMode,
    setColumnMode,
    setModuleMode,
  }
  
  return (
    <>
      <Grid
        item container
        justifyContent="space-between"
        alignItems="center"
        className={classnames("mb-1", classes.sticky)}
      >
        <ButtonSwitch
          size="small"
          value={useFastMode}
          setValue={settings.handleSetUseFastMode}
          containerClassName="py-2"
          values={[
            { value: true, label: t("views.import.modes.fast") },
            { value: false, label: t("views.import.modes.custom") },
          ]}
        />
        { useFastMode && <FastModeConfigurations /> }
      </Grid>
      <Grid
        item container
        className={classnames("w-full m-0 mb-4 px-6 py-4 gap-3 rounded", classes.bordered, classes.sticky)}
      >
        <Grid
          item container
          wrap="nowrap"
          className={classnames("gap-4 overflow-auto scrollbar-visible", classes.scrollableContainer)}
        >
          <TableDataSelector
            availableColumns={availableColumns}
            takenColumns={takenColumns}
            colorColumn={colorColumn}
            {...modesProps}
          />
          <DescriptionColumnSelector
            availableColumns={availableColumns}
            takenColumns={takenColumns}
            colorColumn={colorColumn}
          />
        </Grid>
        <WorkTypeColumnSelector
          availableColumns={availableColumns}
          spreadsheet={spreadsheet}
          takenColumns={takenColumns}
          colorColumn={colorColumn}
          handleColumnSelection={handleColumnSelection}
          hideColumnSelection={useFastMode}
          {...modesProps}
        />
      </Grid>
      {
        !!sheet &&
        <Spreadsheet
          spreadsheet={spreadsheet.current}
          title={sheet.title}
          data={sheet.data.slice()}
          colWidths={sheet.colWidths.slice()}
          columns={sheet.columns.slice()}
          mergeCells={sheet.mergeCells}
          minDimensions={sheet.minDimensions.slice()}
          onRangeSelect={!useFastMode && (
            columnMode !== null
              ? handleColumnSelectionClick
              : handleModuleSelection
          )}
          // initialCellStyles={getInitialCellStyles()}
          setSpreadsheet={setSpreadsheet}
          allowOverflow
        />
      }
    </>
  );
});
